import { useEffect, useState } from 'react';
import MentorPortalNav from './MentorPortalNav';
import MentorMenteesChrome from './MentorMenteesChrome';
import MentorMenteesHeader from './MentorMenteesHeader';
import MentorMenteeCard from './MentorMenteeCard';
import MentorLogEventModal from './MentorLogEventModal';
import MentorPortalService, { MentorMenteeCard as MenteeRow } from './MentorPortalService';
import { EMPTY_MENTOR_COPY } from './mentorDashboardFormat';
import './mentorDashboard.css';
import './mentorMentees.css';
import MentorMenteesLogOfflineInteraction from './MentorMenteesLogOfflineInteraction';
import MentorMenteesMentorshipNotes from './MentorMenteesMentorshipNotes';

const headerFor = (mentees: MenteeRow[]): string => {
    const cohortIds = Array.from(
        new Set(mentees.map((row) => row.cohortId).filter((id): id is number => id != null))
    );
    // GET /mentor/mentees has campusId only (no campus name) and no cohort name — omit, do not invent.
    if (cohortIds.length > 1) {
        return `${cohortIds.length} cohorts`;
    }
    return '';
};

const MentorMentees = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [mentees, setMentees] = useState<MenteeRow[]>([]);
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logPairId, setLogPairId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        MentorPortalService.getMentees()
            .then((data) => {
                if (cancelled) return;
                setMentees(data.mentees || []);
                setLoadError(null);
            })
            .catch((err: { status?: number; response?: { status?: number }; message?: string }) => {
                if (cancelled) return;
                const status = err?.status ?? err?.response?.status;
                if (status === 403 || status === 401) {
                    setMentees([]);
                    setLoadError(null);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const isEmpty = !loading && !loadError && mentees.length === 0;
    const subtitle = headerFor(mentees);
    const canLogOffline = mentees.some((row) => row.canLogOffline === true);

    const reloadMentees = () => {
        MentorPortalService.getMentees()
            .then((data) => {
                setMentees(data.mentees || []);
                setLoadError(null);
            })
            .catch((err: { message?: string }) => {
                setLoadError(err?.message || 'Unable to load mentorship data.');
            });
    };

    const openLog = (pairId: number | null) => {
        setLogPairId(pairId);
        setLogModalOpen(true);
    };

    return (
        <div className="container py-4 mdash">
            
            <MentorPortalNav />
            <MentorMenteesChrome />
            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && loadError && <p className="mdash-empty">{loadError}</p>}
            {isEmpty && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {!loading && !loadError && mentees.length > 0 && (
                <>
                    <MentorMenteesHeader
                        assignedCount={mentees.length}
                        subtitle={subtitle}
                        canLogOffline={canLogOffline}
                        onLogInteraction={() => openLog(null)}
                    />
                    <div className="row g-3 mb-3">
                        {mentees.map((mentee, index) => (
                            <MentorMenteeCard
                                key={mentee.pairId ?? mentee.menteeUuid ?? mentee.menteeName}
                                mentee={mentee}
                                toneIndex={index}
                                onLog={mentee.canLogOffline ? () => openLog(mentee.pairId) : undefined}
                            />
                        ))}
                    </div>
                    <MentorMenteesLogOfflineInteraction/>
                    <MentorMenteesMentorshipNotes/>
                    <MentorLogEventModal
                        show={logModalOpen}
                        mentees={mentees}
                        preselectedPairId={logPairId}
                        onClose={() => setLogModalOpen(false)}
                        onLogged={reloadMentees}
                    />
                </>
            )}
        </div>
    );
};

export default MentorMentees;
